import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Calendar, Flame, Layers, CheckCircle2, AlertTriangle, ChevronRight, Loader2, ArrowLeft,
} from "lucide-react";

const DAY_LABELS = ["L", "M", "M", "J", "V", "S", "D"];

export default function ReviewsDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayCards, setDayCards] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get("/microlearning/flashcards/heatmap?days_ahead=60&days_back=30")
      .then((r) => setData(r.data))
      .finally(() => setBusy(false));
    api.get("/microlearning/flashcards")
      .then((r) => setDayCards(r.data.flashcards || []))
      .catch(() => {});
  }, [user]);

  const cellsByDate = useMemo(() => {
    const m = {};
    (data?.cells || []).forEach((c) => (m[c.date] = c));
    return m;
  }, [data]);

  // Weekly grid: group cells into weeks starting Monday
  const weeks = useMemo(() => {
    if (!data?.cells?.length) return [];
    const first = new Date(data.cells[0].date + "T00:00:00Z");
    const dayIdx = (first.getUTCDay() + 6) % 7; // 0 = Monday
    const padded = Array(dayIdx).fill(null).concat(data.cells);
    const out = [];
    for (let i = 0; i < padded.length; i += 7) out.push(padded.slice(i, i + 7));
    return out;
  }, [data]);

  const cardsForSelectedDate = useMemo(() => {
    if (!selectedDate) return [];
    return dayCards.filter((c) => (c.next_review_at || "").slice(0, 10) === selectedDate);
  }, [selectedDate, dayCards]);

  if (!user) return (
    <div className="max-w-3xl mx-auto px-5 py-24 text-center">
      <p className="text-[#4A5568]">Connectez-vous pour accéder à votre dashboard révisions.</p>
      <Link to="/login?next=/revisions" className="mt-4 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-[#E5276A] text-white text-sm font-bold">Se connecter</Link>
    </div>
  );

  if (busy) return <div className="max-w-6xl mx-auto px-5 py-24 text-center text-[#8B95A1]"><Loader2 className="inline w-5 h-5 animate-spin mr-2" />Chargement…</div>;

  const t = data?.totals || {};

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-10 md:py-14" data-testid="reviews-dashboard">
      <Link to="/microlearning" className="inline-flex items-center gap-1.5 text-xs text-[#4A5568] hover:text-[#E5276A]">
        <ArrowLeft className="w-3.5 h-3.5" /> Retour au microlearning
      </Link>

      <div className="mt-5 flex items-start justify-between flex-wrap gap-3">
        <div>
          <span className="chip-pink inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" /> Study Schedule</span>
          <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-3 leading-[1.05]">
            Study <span className="text-[#E5276A]">Schedule</span>
          </h1>
          <p className="text-[#4A5568] mt-2 max-w-2xl">
            La courbe de l&apos;oubli d&apos;Ebbinghaus : les cartes reviennent au moment optimal pour ancrer les connaissances à long terme.
          </p>
        </div>
        {typeof t.streak_days === "number" && t.streak_days > 0 && (
          <div className="flex items-center gap-2">
            <div
              data-testid="study-streak-badge"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl bg-gradient-to-br from-[#FF6B35] to-[#F9C548] text-white shadow-md"
              title="Jours consécutifs de révision"
            >
              <Flame className="w-5 h-5" fill="white" />
              <div className="leading-tight">
                <div className="font-display text-2xl">{t.streak_days}</div>
                <div className="text-[9px] font-bold uppercase tracking-widest opacity-90">day streak</div>
              </div>
            </div>
            {typeof t.streak_freezes === "number" && t.streak_freezes > 0 && (
              <div
                data-testid="streak-freeze-badge"
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-sky-100 text-sky-700 border border-sky-200"
                title="Streak freezes disponibles — pardonne 1 jour manqué"
              >
                <span className="text-base leading-none">❄️</span>
                <span className="text-xs font-bold tabular-nums">{t.streak_freezes}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* KPI */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={<AlertTriangle className="w-4 h-4 text-red-500" />} label="Aujourd'hui" value={t.due_today} sub={t.due_today > 0 ? "à réviser" : "🎉 rien de dû"} testId="rev-kpi-today" />
        <Kpi icon={<Flame className="w-4 h-4 text-orange-500" />} label="En retard" value={t.overdue} sub={t.overdue > 0 ? "à rattraper" : "aucun"} testId="rev-kpi-overdue" />
        <Kpi icon={<Layers className="w-4 h-4 text-[#1F3A5F]" />} label="Deck total" value={t.deck_size} sub={`${t.next_7_days} dans 7j`} testId="rev-kpi-deck" />
        <Kpi icon={<CheckCircle2 className="w-4 h-4 text-emerald-600" />} label="Maîtrisées" value={t.mastered} sub="archivées" testId="rev-kpi-mastered" />
      </div>

      {/* Grid + right pane */}
      <div className="mt-8 grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 app-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] uppercase tracking-widest text-[#8B95A1] font-bold">Heat-map SM-2 — 90 jours</div>
            <Link to="/microlearning" className="text-[11px] font-bold text-[#E5276A] hover:underline inline-flex items-center gap-1">
              Lancer une révision <ChevronRight className="w-3 h-3" />
            </Link>
          </div>

          <div className="flex items-start gap-3 overflow-x-auto pb-2">
            <div className="flex flex-col justify-around pt-6 mr-1 shrink-0" style={{ height: (weeks.length ? weeks[0]?.length : 7) * 20 }}>
              {DAY_LABELS.map((d, i) => (
                <span key={i} className="text-[9px] text-[#8B95A1]" style={{ height: 20, lineHeight: "20px" }}>{d}</span>
              ))}
            </div>
            <div className="flex gap-1" data-testid="rev-heatmap">
              {weeks.map((week, wi) => (
                <div key={wi} className="flex flex-col gap-1">
                  {week.map((cell, di) => {
                    if (!cell) return <div key={di} className="w-4 h-4" />;
                    const intensity = cellIntensity(cell.due, t.due_today || 0);
                    const bg = cell.is_overdue ? "#DC2626" : intensity.bg;
                    const border = cell.is_today ? "#1F3A5F" : "transparent";
                    const isSel = selectedDate === cell.date;
                    return (
                      <button
                        key={cell.date}
                        onClick={() => setSelectedDate(cell.date)}
                        title={`${cell.date} · ${cell.due} carte${cell.due > 1 ? "s" : ""}`}
                        data-testid={`rev-cell-${cell.date}`}
                        className="w-4 h-4 rounded-sm transition-transform hover:scale-125"
                        style={{ background: bg, border: `2px solid ${isSel ? "#E5276A" : border}` }}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 flex items-center gap-3 text-[10px] text-[#8B95A1] flex-wrap">
            <span>Moins</span>
            {[0.05, 0.2, 0.4, 0.7, 1].map((v, i) => (
              <span key={i} className="w-3 h-3 rounded-sm inline-block" style={{ background: cellIntensity(v * 10, 10).bg }} />
            ))}
            <span>Plus</span>
            <span className="w-3 h-3 rounded-sm inline-block ml-4" style={{ background: "#DC2626" }} />
            <span>En retard</span>
            <span className="w-3 h-3 rounded-sm inline-block border-2 border-[#1F3A5F] ml-4" />
            <span>Aujourd&apos;hui</span>
          </div>
        </div>

        <div className="app-card p-5" data-testid="rev-sidebar">
          {selectedDate ? (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-[#E5276A] font-bold">
                {formatDate(selectedDate)}
              </div>
              <div className="font-display text-3xl text-[#1F3A5F] mt-1">
                {cardsForSelectedDate.length} <span className="text-base text-[#4A5568] font-light">carte{cardsForSelectedDate.length > 1 ? "s" : ""}</span>
              </div>
              <div className="mt-3 text-xs text-[#4A5568]">
                {cellsByDate[selectedDate]?.is_overdue ? "⚠ En retard — à rattraper" :
                 cellsByDate[selectedDate]?.is_today ? "Programmées pour aujourd'hui" :
                 cellsByDate[selectedDate]?.is_past ? "Passées" : "À venir"}
              </div>

              {cardsForSelectedDate.length > 0 ? (
                <>
                  <div className="mt-4 space-y-2 max-h-96 overflow-auto">
                    {cardsForSelectedDate.slice(0, 8).map((c) => (
                      <div key={c.id} className="rounded-lg bg-[#FBF7F3] p-3">
                        <div className="text-[10px] font-bold text-[#E5276A] uppercase tracking-wider">
                          {c.source_label || (c.source || "").replace("academy:", "🎓 ").replace("deck:", "📚 ")}
                        </div>
                        <div className="text-sm text-[#1F3A5F] mt-1 font-medium">{c.question}</div>
                      </div>
                    ))}
                    {cardsForSelectedDate.length > 8 && (
                      <div className="text-[10px] text-[#8B95A1] text-center">+ {cardsForSelectedDate.length - 8} autres</div>
                    )}
                  </div>
                  {(cellsByDate[selectedDate]?.is_today || cellsByDate[selectedDate]?.is_overdue) && (
                    <Link to="/microlearning" data-testid="rev-goto-review"
                      className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661]">
                      Réviser maintenant <ChevronRight className="w-4 h-4" />
                    </Link>
                  )}
                </>
              ) : (
                <p className="mt-4 text-sm text-[#8B95A1]">Aucune carte planifiée ce jour-là.</p>
              )}
            </div>
          ) : (
            <div>
              <div className="w-12 h-12 rounded-xl bg-[#FCE4ED] flex items-center justify-center">
                <Calendar className="w-6 h-6 text-[#E5276A]" />
              </div>
              <p className="mt-3 font-bold text-[#1F3A5F]">Cliquez sur une case</p>
              <p className="text-sm text-[#4A5568] mt-1">Explorez ce qui vous attend, ou ce que vous avez déjà maîtrisé.</p>
              {t.due_today > 0 && (
                <Link to="/microlearning" data-testid="rev-cta-today"
                  className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661]">
                  {t.due_today} carte{t.due_today > 1 ? "s" : ""} à réviser <ChevronRight className="w-4 h-4" />
                </Link>
              )}
              {t.overdue > 0 && (
                <div className="mt-3 rounded-xl bg-red-50 border-l-4 border-red-500 p-3">
                  <div className="inline-flex items-center gap-1.5 text-xs font-bold text-red-700">
                    <AlertTriangle className="w-3.5 h-3.5" /> {t.overdue} carte{t.overdue > 1 ? "s" : ""} en retard
                  </div>
                  <p className="text-xs text-red-800 mt-1">Rattrapez-les pour maintenir votre progression.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, sub, testId }) {
  return (
    <div className="app-card p-4" data-testid={testId}>
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-[10px] uppercase tracking-widest text-[#8B95A1] font-bold">{label}</span>
      </div>
      <div className="font-display text-3xl text-[#1F3A5F] mt-1.5 leading-none">{value}</div>
      {sub && <div className="text-[10px] text-[#8B95A1] mt-1">{sub}</div>}
    </div>
  );
}

function cellIntensity(count, dueToday) {
  const max = Math.max(dueToday, 5);
  const t = Math.min(1, count / max);
  if (count === 0) return { bg: "#EDF2F7" };
  if (t < 0.25) return { bg: "#FCE4ED" };
  if (t < 0.5) return { bg: "#F9A5C4" };
  if (t < 0.75) return { bg: "#E5276A" };
  return { bg: "#C01661" };
}

function formatDate(iso) {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  } catch { return iso; }
}
